import { isDipsUrlConfigured } from '~/clients/dips-client'
import {
    importAgeGroups,
    importDepartmentLocationAssignments,
    importDepartmentPractitionerAssignments,
    importDepartments,
    importHospitalSurgeryTypeGroupAssociations,
    importHospitalSurgeryTypes,
    importLocations,
    importPlanningPeriods,
    importPractitioners,
    importPractitionerServicePeriods,
    importPractitionerStatusDefinitions,
    importSpecialities,
    importSurgeryTypeGroupAgeRestrictions,
    importSurgeryTypeGroupHierarchies,
    importSurgeryTypeGroups,
    importSurgeryTypeGroupSpecialities,
} from '~/store/di-entity.api'
import { useStore } from '~/store/store'

import { useMultiQuery } from '..'

/**
 * Fetch all base entities (stable data) that is prevalent throughout the application. The criteria to include data here is:
 * - The data is relatively stable (doesn't update too often)
 * - The data is used in multiple places in the application
 * - The data is not schedule-specific, meaning it doesn't change based on the selected date
 *
 * The options args passed to the `useMultiQuery` hook and the globally configured `gcTime` of `Infinity` should prevent any
 * refetching of this data once loaded. And `isPending` will only be `true` on the initial load.
 */
export const useBaseEntitiesQueries = () => {
    const dipsApiAccessToken = useStore(state => state.user.dipsApiAccessToken)
    const isDeepinsightUser = useStore(state => state.user.issuer === 'google')

    const shouldWaitForDipsAccessToken = isDipsUrlConfigured() && dipsApiAccessToken === null && !isDeepinsightUser

    const { isPending, isError } = useMultiQuery(
        [
            { queryFn: () => importDepartments({ exclude_locations: true, exclude_practitioners: true, exclude_sections: true }), queryKey: ['departments'] },

            { queryFn: () => importLocations({}), queryKey: ['locations'] },
            { queryFn: () => importDepartmentLocationAssignments({}), queryKey: ['departmentLocationAssignments'] },

            { queryFn: () => importPractitioners({}), queryKey: ['practitioners'] },
            { queryFn: () => importDepartmentPractitionerAssignments({}), queryKey: ['departmentPractitionerAssignments'] },
            { queryFn: () => importPractitionerStatusDefinitions({}), queryKey: ['practitionerStatusDefinitions'] },
            { queryFn: () => importPractitionerServicePeriods({}), queryKey: ['practitionerServicePeriods'] },

            { queryFn: () => importSpecialities({}), queryKey: ['specialities'] },
            { queryFn: () => importSurgeryTypeGroupSpecialities({}), queryKey: ['surgeryTypeGroupSpecialities'] },
            { queryFn: () => importSurgeryTypeGroupHierarchies({}), queryKey: ['surgeryTypeGroupHierarchies'] },
            { queryFn: () => importSurgeryTypeGroups({}), queryKey: ['surgeryTypeGroups'] },
            { queryFn: () => importSurgeryTypeGroupAgeRestrictions({}), queryKey: ['surgeryTypeGroupAgeRestrictions'] },
            { queryFn: () => importAgeGroups({}), queryKey: ['ageGroups'] },

            { queryFn: () => importPlanningPeriods({}), queryKey: ['planningPeriods'] },

            { queryFn: () => importHospitalSurgeryTypeGroupAssociations({ exclude_surgery_type: true }), queryKey: ['hospitalSurgeryTypeGroupAssociations'] },
            { queryFn: () => importHospitalSurgeryTypes({}), queryKey: ['hospitalSurgeryTypes'] },
        ],
        {
            skip: shouldWaitForDipsAccessToken,
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            showToast: false, // error toasts are handled separately in AuthenticatedAppContents
        }
    )

    return { isPending, isError }
}
